import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithoutRef, ElementType } from 'react'
import { cn } from '../../lib/utils'

const surfaceCardVariants = cva(
  'surface-card landing-feed-card rounded-xl border border-zinc-200 bg-white text-zinc-950 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50',
  {
    variants: {
      size: {
        default: 'p-5 sm:p-6',
        compact: 'p-4 sm:p-5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

const surfacePanelVariants = cva(
  'surface-panel landing-surface-soft rounded-xl border border-zinc-200 bg-zinc-50/70 p-4 text-zinc-950 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-50'
)

const neutralPanelVariants = cva(
  'rounded-xl border border-zinc-200 dark:border-zinc-700',
  {
    variants: {
      tone: {
        default: 'bg-zinc-50 dark:bg-zinc-900/60',
        muted: 'bg-zinc-50/70 dark:bg-zinc-900/50',
        info: 'bg-zinc-50 text-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-300',
        solid: 'bg-white dark:bg-zinc-950',
      },
    },
    defaultVariants: {
      tone: 'default',
    },
  }
)

const surfaceRowVariants = cva(
  'surface-row landing-surface-soft rounded-lg border border-zinc-200 bg-zinc-50/80 px-4 py-3 text-zinc-950 shadow-sm transition-colors dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-50'
)

type SurfaceProps<T extends ElementType, Props = {}> = Props & {
  as?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof Props | 'as'>

type SurfaceCardProps<T extends ElementType> = SurfaceProps<
  T,
  VariantProps<typeof surfaceCardVariants>
>
type NeutralPanelProps<T extends ElementType> = SurfaceProps<
  T,
  VariantProps<typeof neutralPanelVariants>
>

export const surfaceRowClassName = surfaceRowVariants()

export function SurfaceCard<T extends ElementType = 'div'>({
  as,
  className,
  size,
  ...props
}: SurfaceCardProps<T>) {
  const Component = as ?? 'div'

  return (
    <Component
      className={cn(surfaceCardVariants({ size }), className)}
      {...props}
    />
  )
}

export function SurfacePanel<T extends ElementType = 'div'>({
  as,
  className,
  ...props
}: SurfaceProps<T>) {
  const Component = as ?? 'div'

  return (
    <Component
      className={cn(surfacePanelVariants(), className)}
      {...props}
    />
  )
}

export function SurfaceRow<T extends ElementType = 'div'>({
  as,
  className,
  ...props
}: SurfaceProps<T>) {
  const Component = as ?? 'div'

  return (
    <Component
      className={cn(surfaceRowVariants(), className)}
      {...props}
    />
  )
}

export function NeutralPanel<T extends ElementType = 'div'>({
  as,
  className,
  tone,
  ...props
}: NeutralPanelProps<T>) {
  const Component = as ?? 'div'

  return (
    <Component
      className={cn(neutralPanelVariants({ tone }), className)}
      {...props}
    />
  )
}
